// src/routes/merchants/catalogue/BrandSuggestion.tsx
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Button } from '../../../platform/primitives/Button'
import { Panel } from '../../../platform/primitives/Panel'
import type { PickerMerchant } from '../picker/merchantPickerModel'
import { BrandConfirm } from './BrandConfirm'
import type { PickExtras } from './catalogueModel'
import { useNotNow } from './notNow'
import { useBrandSuggestion } from './useCatalogue'
import './brands.css'

/**
 * "Looks like **Tim Hortons**: add as a merchant?" [Add] [Not now] — owner decision C4 (#494).
 *
 * Shown whenever `/match` names a brand for `name` (the server applies the threshold and hides brands the
 * user already has), unless every transaction in `txnIds` turned that brand down. **It never adds
 * anything by itself**: Add opens the same step the picker's Brands use (`BrandConfirm`), with its share
 * and category options, and only that step's Add creates the merchant; `onAdded` then assigns it.
 *
 * Under `custom_merchants`, like the picker's Brands: adding one creates the user's own merchant. Without
 * it nothing is suggested and `/match` is not asked (`useBrandSuggestion` checks). A surface's own offer,
 * not a surface: there is no loading or error state to show, so a suggestion that cannot be made is absent.
 *
 * **Focus.** Cancel in the add step returns focus to this suggestion's Add. "Not now" and a successful Add
 * take the suggestion (or its step) off the screen with focus inside it, so `returnFocus` is called then:
 * the caller knows what stays on its screen (the merchant field, the next suggestion). `addRef` hands the
 * caller this suggestion's Add, for a neighbour's `returnFocus` to land on.
 *
 * Outline buttons: the screens it sits on already have their primary action.
 *
 * `panelClassName` draws it as its own `Panel` (a page section, as on Cashflow); without it, it is a bare
 * section inside the caller's own panel (the transaction's details).
 */
export function BrandSuggestion({
  name,
  txnIds,
  offerCategory,
  onAdded,
  returnFocus,
  addRef,
  panelClassName,
}: {
  /** The transaction's cleaned merchant name, what `/match` is asked about. */
  name: string
  /** The transaction(s) the suggestion is for: one on a detail page, a group of rows on Cashflow. */
  txnIds: readonly string[]
  /** Every transaction here has no category: offer the brand's suggested one. */
  offerCategory: boolean
  onAdded: (merchant: PickerMerchant, extras?: PickExtras) => void
  /** Move focus somewhere that stays: called when "Not now" or a successful Add removes the suggestion. */
  returnFocus: () => void
  addRef?: (el: HTMLButtonElement | null) => void
  panelClassName?: string
}) {
  const brand = useBrandSuggestion(name)
  const notNow = useNotNow()
  const [adding, setAdding] = useState<string | null>(null)
  // Cancel returns focus to Add, once the closed step uncovers it.
  const addButton = useRef<HTMLButtonElement | null>(null)
  const refocusAdd = useRef(false)
  useEffect(() => {
    if (adding !== null || !refocusAdd.current) return
    refocusAdd.current = false
    addButton.current?.focus()
  }, [adding])
  if (brand === null || notNow.hides(txnIds, brand.id)) return null
  const setAddRef = (el: HTMLButtonElement | null) => {
    addButton.current = el
    addRef?.(el)
  }
  const body = (
    <>
      {adding === brand.id ? (
        <BrandConfirm
          brand={brand}
          offerCategory={offerCategory}
          onAdded={(m, extras) => {
            if (extras === undefined) onAdded(m)
            else onAdded(m, extras)
            returnFocus()
          }}
          onCancel={() => {
            refocusAdd.current = true
            setAdding(null)
          }}
          addVariant="outline"
        />
      ) : (
        <>
          <p className="brand-suggestion__text">
            Looks like <strong>{brand.name}</strong>: add as a merchant?
          </p>
          <div className="brand-suggestion__actions">
            <Button ref={setAddRef} type="button" variant="outline" size="panel" onClick={() => setAdding(brand.id)}>
              Add
            </Button>
            <Button type="button" variant="chip" size="panel" onClick={() => {
                notNow.dismiss(txnIds, brand.id)
                returnFocus()
              }}
            >
              Not now
            </Button>
          </div>
        </>
      )}
    </>
  )
  const frame = (children: ReactNode) =>
    panelClassName === undefined ? (
      <section className="brand-suggestion" aria-label="Merchant suggestion">
        {children}
      </section>
    ) : (
      <Panel className={`brand-suggestion ${panelClassName}`} role="region" aria-label="Merchant suggestion">
        {children}
      </Panel>
    )
  return frame(body)
}
